import React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { busyLabel, clipToDay, firstUncoveredMinute, type BusyBlock } from "@/lib/calendar/externalBusy";

type Span = { startMin: number; endMin: number };

/**
 * A master's busy time held outside Boona, drawn on her journal column (boona
 * docs/codebase/google-calendar/README.md §11): a quiet hatched band per block, under the
 * appointment cards (styles: `.calendar-busy*` in index.css).
 *
 * It is not an appointment: nothing opens it and nothing drags it. Clicks pass through to the
 * slot beneath, so an admin can still book there (the modal then warns, it never refuses). Only the
 * label and a conflict's edge take the pointer, for their tooltips.
 *
 * Placement follows the cards: one hour per `--slot-h`, counted from the grid's first hour, in a
 * layer with the card overlay's insets. A block outside the drawn hours is clipped or left out.
 * The label goes to the first stretch of the band no card covers, so a conflict still says what
 * it is below the appointment it overlaps.
 */
export const BusyBlocksLayer: React.FC<{
  /** This column's blocks: one master, one day. */
  blocks: readonly BusyBlock[];
  /** The column's appointment cards (minutes of the day), drawn over the bands. */
  cards?: readonly Span[];
  dayStartHour: number;
  dayEndHour: number;
}> = ({ blocks, cards, dayStartHour, dayEndHour }) => {
  const { t } = useTranslation();
  const dayStartMin = dayStartHour * 60;
  const dayEndMin = dayEndHour * 60;

  const bands = blocks
    .map((block) => ({ block, shown: clipToDay(block, dayStartMin, dayEndMin) }))
    .filter((band): band is { block: BusyBlock; shown: Span } => band.shown !== null);
  if (bands.length === 0) return null;

  const conflictText = t("calendar.busy.conflict", "Overlaps an appointment");
  return (
    <div className="calendar-busy-layer">
      {bands.map(({ block, shown }) => {
        const label = busyLabel(block.provider, t);
        const hint = block.conflict ? `${label} — ${conflictText}` : label;
        const labelOffsetMin = (firstUncoveredMinute(shown, cards) ?? shown.startMin) - shown.startMin;
        return (
          <div
            key={block.id}
            className={block.conflict ? "calendar-busy calendar-busy--conflict" : "calendar-busy"}
            data-busy-provider={block.provider}
            data-busy-status={block.conflict ? "conflict" : "active"}
            style={{
              top: `calc(var(--slot-h) * ${(shown.startMin - dayStartMin) / 60})`,
              height: `calc(var(--slot-h) * ${(shown.endMin - shown.startMin) / 60})`,
            }}
          >
            {block.conflict && <span className="calendar-busy__edge" title={hint} aria-hidden="true" />}
            <span
              className="calendar-busy__label"
              title={hint}
              style={labelOffsetMin > 0 ? { top: `calc(var(--slot-h) * ${labelOffsetMin / 60} + 1px)` } : undefined}
            >
              {block.conflict && <AlertTriangle className="h-[1em] w-[1em] shrink-0" aria-hidden="true" />}
              <span className="min-w-0 truncate">{label}</span>
              {block.conflict && <span className="sr-only">{conflictText}</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
};
